import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaQuestionCircle } from 'react-icons/fa';
import '../assets/css/FAQ.css';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setIsMobile(width <= 767);
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      setTimeout(handleResize, 100);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize]);

  // FAQ data
  const faqData = [
    {
      id: 1,
      question: "Do you offer custom-made office furniture?",
      answer: "Yes, at Design Craft, we specialize in designing and manufacturing custom-made office furniture tailored to your workspace, brand identity, and functional needs. Share your requirements and we'll build to match your vision."
    },
    {
      id: 2,
      question: "What types of office furniture do you provide?",
      answer: "We offer a wide range of products including office desks, workstations, office chairs, reception desks, meeting tables, storage units, and more — all crafted with precision and designed for modern workspaces."
    },
    {
      id: 3,
      question: "Can you handle complete office fit-out projects?",
      answer: "Absolutely. Design Craft offers end-to-end office fit-out services — from space planning and interior design to furniture manufacturing and final installation, ensuring a seamless and efficient transformation."
    },
    {
      id: 4,
      question: "What materials do you use in your furniture?",
      answer: "We use high-quality materials including engineered wood, MDF, solid wood, metal, glass, and premium upholstery to ensure durability, comfort, and aesthetic appeal in every piece."
    },
    {
      id: 5,
      question: "Do you deliver and install furniture across the UAE?",
      answer: "Yes, we deliver and install all our furniture across Dubai and the UAE. Our team ensures professional handling, on-time delivery, and hassle-free installation at your site."
    }
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    e.currentTarget.style.transform = 'scale(0.98)';
    e.currentTarget.style.transition = 'transform 0.1s ease';
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    setTimeout(() => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.transition = 'transform 0.3s ease';
    }, 100);
  };

  return (
    <div className="faq-page">
      <div className="faq-container">
        {/* Header Section */}
        <div className="faq-header">
          <div className="faq-icon">
            <FaQuestionCircle />
          </div>
          <h1 className="faq-title">
            {isMobile ? 'FAQ' : 'Frequently Asked Questions'}
          </h1>
          <p className="faq-subtitle">
            {isMobile 
              ? 'Find answers to common questions about our services and products.'
              : 'Everything you need to know about Design Craft. Can\'t find the answer you\'re looking for? Feel free to reach out to our team.'
            }
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="faq-accordion">
          {faqData.map((faq, index) => (
            <div 
              key={faq.id} 
              className={`faq-item ${activeIndex === index ? 'active' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleAccordion(index)}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${faq.id}`}
              >
                <span className="question-text">{faq.question}</span>
                <span className="faq-icon">
                  {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </button>
              
              <div 
                id={`faq-answer-${faq.id}`}
                className={`faq-answer ${activeIndex === index ? 'expanded' : ''}`}
              >
                <div className="answer-content">
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="faq-cta">
          <h3>Still have questions?</h3>
          <p>
            {isMobile 
              ? 'Contact our team for personalized assistance.'
              : 'Can\'t find what you\'re looking for? Our friendly team is here to help you with any questions about our products and services.'
            }
          </p>
          <div className="cta-buttons">
            <a 
              href="tel:+971501989883" 
              className="cta-button primary"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              Call Us
            </a>
            <Link 
              to="/contact" 
              className="cta-button secondary"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              Email Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ; 